import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Check,
  Pencil,
  Lock,
  Search,
  Power,
  PowerOff,
  Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import { Input, Select, Modal, Button, Badge } from '../../Components/ui';
import { useConfirm } from '../../Components/Feedback';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { API_URL } from '../../api/config';

const PERFIS = [
  { value: 'monitor', label: 'Monitor' },
  { value: 'professor', label: 'Educador' },
  { value: 'coordenador', label: 'Coordenador' },
  { value: 'master', label: 'Master' },
];

// Painel de administração de usuários — só acessível a perfil "master" (o
// redirect em useEffect abaixo expulsa qualquer outro perfil). Reúne 3
// responsabilidades: aprovar/rejeitar cadastros pendentes, CRUD de usuários e
// redefinição de senha administrativa (sem precisar da senha atual do usuário).
function AdminUsuarios() {
  const { user, token } = useAuth();
  const { institutionId, institutionName } = useInstitution();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosPendentes, setUsuariosPendentes] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [perfisCustomizados, setPerfisCustomizados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [editando, setEditando] = useState(null);
  const [modalNovoAberto, setModalNovoAberto] = useState(false);
  const [senhaModal, setSenhaModal] = useState(null);
  const [alternandoStatusId, setAlternandoStatusId] = useState(null);

  // --- Busca e filtros da lista de usuários cadastrados ---
  const [busca, setBusca] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('');
  const [filtroInstituicao, setFiltroInstituicao] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [form, setForm] = useState({
    nome: '',
    email: '',
    senha: '',
    perfil: 'monitor',
    instituicoes: user?.perfil !== 'master' && institutionId ? [Number(institutionId)] : [],
    id_professor: '',
    area_coordenacao: '',
  });
  const [professoresDisponiveis, setProfessoresDisponiveis] = useState([]);

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-institution-id': institutionId,
    }),
    [token, institutionId],
  );

  const carregarDados = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [usuariosRes, pendentesRes, instituicoesRes, perfisRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/admin/usuarios`, { headers: { Authorization: `Bearer ${token}`, 'x-institution-id': institutionId } }),
        fetch(`${API_URL}/api/auth/admin/usuarios/pendentes`, { headers: { Authorization: `Bearer ${token}`, 'x-institution-id': institutionId } }),
        fetch(`${API_URL}/api/auth/instituicoes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/perfis-customizados`, {
          headers: { Authorization: `Bearer ${token}`, 'x-institution-id': institutionId },
        }),
      ]);

      const usuariosData = await usuariosRes.json();
      const pendentesData = await pendentesRes.json();
      const instituicoesData = await instituicoesRes.json();
      const perfisData = await perfisRes.json();

      if (!usuariosRes.ok) throw new Error(usuariosData?.error || 'Erro ao carregar usuários');
      if (!pendentesRes.ok) throw new Error(pendentesData?.error || 'Erro ao carregar pendentes');
      if (!instituicoesRes.ok)
        throw new Error(instituicoesData?.error || 'Erro ao carregar instituições');

      const rawUsuarios = usuariosData?.data ?? usuariosData;
      const rawPendentes = pendentesData?.data ?? pendentesData;
      const rawInstituicoes = instituicoesData?.data ?? instituicoesData;
      const rawPerfis = perfisData?.data ?? perfisData;

      setUsuarios(Array.isArray(rawUsuarios) ? rawUsuarios : []);
      setUsuariosPendentes(Array.isArray(rawPendentes) ? rawPendentes : []);
      const instLista = Array.isArray(rawInstituicoes) ? rawInstituicoes : [];
      const instFiltradas =
        user?.perfil === 'master'
          ? instLista
          : instLista.filter((i) =>
              Array.isArray(user?.instituicoes)
                ? user.instituicoes.includes(i.id)
                : Number(i.id) === Number(institutionId),
            );
      setInstituicoes(instFiltradas);
      setPerfisCustomizados(perfisRes.ok && Array.isArray(rawPerfis) ? rawPerfis : []);
    } catch (error) {
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  }, [token, institutionId]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    carregarDados();
  }, [user, navigate, carregarDados]);

  // Busca os professores cadastrados nas instituições selecionadas no
  // formulário — só usado quando o perfil é "professor", pro seletor de
  // vínculo abaixo. Refaz sempre que a lista de instituições do form muda.
  useEffect(() => {
    if (form.perfil !== 'professor' || form.instituicoes.length === 0) {
      setProfessoresDisponiveis([]);
      return;
    }
    let cancelado = false;
    (async () => {
      const listas = await Promise.all(
        form.instituicoes.map(async (idInst) => {
          const res = await fetch(
            `${API_URL}/api/auth/admin/professores?id_instituicao=${idInst}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (!res.ok) return [];
          const rawProf = await res.json();
          return rawProf?.data ?? (Array.isArray(rawProf) ? rawProf : []);
        }),
      );
      if (!cancelado) setProfessoresDisponiveis(listas.flat());
    })();
    return () => {
      cancelado = true;
    };
  }, [form.perfil, form.instituicoes, token]);

  const resetForm = () => {
    setForm({
      nome: '',
      email: '',
      senha: '',
      perfil: 'monitor',
      instituicoes: [],
      id_professor: '',
      area_coordenacao: '',
    });
    setEditando(null);
    setModalNovoAberto(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setMensagem('');

    if (form.senha && form.senha.length < 6) {
      setErro('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    const body = {
      ...form,
      instituicoes: form.instituicoes.map(Number),
      id_professor:
        form.perfil === 'professor' && form.id_professor ? Number(form.id_professor) : null,
      area_coordenacao:
        form.perfil === 'coordenador' && form.area_coordenacao ? form.area_coordenacao : null,
    };
    if (editando) {
      // Ao editar, senha é opcional: só envia o campo se o admin preencheu algo
      // (o backend mantém a senha atual quando o campo não vem no PUT).
      if (!form.senha) delete body.senha;
      await atualizarUsuario(editando, body);
    } else {
      if (!form.senha) {
        setErro('Defina uma senha para o novo usuário.');
        return;
      }
      await criarUsuario(body);
    }
  };

  const criarUsuario = async (body) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao criar usuário');
      setMensagem('Usuário criado com sucesso.');
      resetForm();
      carregarDados();
    } catch (error) {
      setErro(error.message);
    }
  };

  const atualizarUsuario = async (id, body) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios/${id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao atualizar usuário');
      setMensagem('Usuário atualizado com sucesso.');
      resetForm();
      carregarDados();
    } catch (error) {
      setErro(error.message);
    }
  };

  const redefinirSenha = async (id, senha) => {
    setErro('');
    setMensagem('');
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios/${id}/senha`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ senha }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao redefinir senha');
      setMensagem('Senha redefinida com sucesso.');
      setSenhaModal(null);
    } catch (error) {
      setErro(error.message);
    }
  };

  const aprovarUsuario = async (id) => {
    setErro('');
    setMensagem('');
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios/${id}/aprovar`, {
        method: 'PUT',
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao aprovar usuário');
      setMensagem('Usuário aprovado com sucesso.');
      carregarDados();
    } catch (error) {
      setErro(error.message);
    }
  };

  const rejeitarUsuario = async (id) => {
    if (
      !(await confirm('Tem certeza que deseja rejeitar este cadastro? O usuário será removido.', {
        title: 'Rejeitar cadastro',
        confirmText: 'Rejeitar',
      }))
    )
      return;
    setErro('');
    setMensagem('');
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios/${id}/rejeitar`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao rejeitar usuário');
      setMensagem('Cadastro rejeitado com sucesso.');
      carregarDados();
    } catch (error) {
      setErro(error.message);
    }
  };

  // Ativa/desativa sem precisar abrir o modal de edição — o PUT é "substitui
  // tudo", então reenvia os dados atuais do usuário junto com o status novo
  // (senha fica de fora: sem o campo no corpo, o backend mantém a atual).
  const alternarStatusUsuario = async (u) => {
    const novoStatus = u.status === 'ativo' ? 'inativo' : 'ativo';
    const acao = novoStatus === 'ativo' ? 'reativar' : 'desativar';
    if (
      !(await confirm(`Tem certeza que deseja ${acao} "${u.nome}"?`, {
        title: `${acao === 'reativar' ? 'Reativar' : 'Desativar'} usuário`,
        confirmText: acao === 'reativar' ? 'Reativar' : 'Desativar',
      }))
    )
      return;
    setAlternandoStatusId(u.id);
    setErro('');
    setMensagem('');
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/usuarios/${u.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          nome: u.nome,
          email: u.email,
          perfil: u.perfil,
          instituicoes: u.instituicoes,
          id_professor: u.id_professor,
          area_coordenacao: u.area_coordenacao,
          status: novoStatus,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Erro ao ${acao} usuário`);
      setMensagem(`Usuário ${novoStatus === 'ativo' ? 'reativado' : 'desativado'} com sucesso.`);
      carregarDados();
    } catch (error) {
      setErro(error.message);
    } finally {
      setAlternandoStatusId(null);
    }
  };

  const iniciarEdicao = (u) => {
    setEditando(u.id);
    setForm({
      nome: u.nome,
      email: u.email,
      senha: '',
      perfil: u.perfil,
      instituicoes: Array.isArray(u.instituicoes) ? u.instituicoes : [],
      id_professor: u.id_professor || '',
      area_coordenacao: u.area_coordenacao || '',
    });
  };

  const toggleInstituicao = (id) => {
    const idInt = Number(id);
    setForm((prev) => {
      const possui = prev.instituicoes.includes(idInt);
      return {
        ...prev,
        instituicoes: possui
          ? prev.instituicoes.filter((i) => i !== idInt)
          : [...prev.instituicoes, idInt],
      };
    });
  };

  const nomeInstituicao = (id) => instituicoes.find((i) => i.id === id)?.nome || `ID ${id}`;

  // Perfis fixos + os que o master já criou pela tela de Permissões (ver
  // backend/perfis-customizados.js) — um perfil customizado é "genérico", não
  // ganha os campos de Educador/Coordenador abaixo (só eles têm vínculo
  // especial com professor/área).
  const todosPerfis = useMemo(() => {
    const base = user?.perfil === 'master' ? PERFIS : PERFIS.filter((p) => p.value !== 'master');
    return [...base, ...perfisCustomizados.map((p) => ({ value: p.chave, label: p.nome }))];
  }, [perfisCustomizados, user?.perfil]);

  // Busca por nome/e-mail (sem acento/maiúscula) + filtros por perfil,
  // instituição e status — tudo client-side, a lista já vem inteira do
  // backend e dificilmente passa de umas poucas centenas de linhas.
  const normalizar = (s) =>
    String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const buscaNorm = normalizar(busca.trim());
  const usuariosFiltrados = useMemo(
    () =>
      usuarios.filter((u) => {
        if (user?.perfil !== 'master' && u.perfil === 'master') return false;
        if (
          buscaNorm &&
          !normalizar(u.nome).includes(buscaNorm) &&
          !normalizar(u.email).includes(buscaNorm)
        )
          return false;
        if (filtroPerfil && u.perfil !== filtroPerfil) return false;
        if (filtroStatus && u.status !== filtroStatus) return false;
        if (filtroInstituicao && !(u.instituicoes || []).includes(Number(filtroInstituicao)))
          return false;
        return true;
      }),
    [usuarios, buscaNorm, filtroPerfil, filtroStatus, filtroInstituicao],
  );

  const filtrosAtivos = !!(busca.trim() || filtroPerfil || filtroStatus || filtroInstituicao);
  const limparFiltros = () => {
    setBusca('');
    setFiltroPerfil('');
    setFiltroStatus('');
    setFiltroInstituicao('');
  };

  // Campos compartilhados entre o card de "Novo Usuário" (inline) e o modal de
  // "Editar Usuário" — evita duplicar o JSX dos dois formulários.
  const camposFormulario = (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome"
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          required
        />
        <Input
          type="email"
          label="E-mail"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <Select
          label="Perfil"
          value={form.perfil}
          onChange={(e) => setForm({ ...form, perfil: e.target.value })}
          options={todosPerfis.map((p) => ({ value: p.value, label: p.label }))}
        />
        <Input
          type="password"
          label={editando ? 'Nova senha (deixe em branco para manter)' : 'Senha'}
          value={form.senha}
          onChange={(e) => setForm({ ...form, senha: e.target.value })}
          required={!editando}
          minLength={editando ? undefined : 6}
        />
      </div>
      {perfisCustomizados.some((p) => p.chave === form.perfil) && (
        <p className="text-xs text-amber-600 dark:text-amber-400 -mt-2">
          Perfil customizado específico de "{institutionName}" — só funciona para instituições
          vinculadas onde ele também existe (crie-o nelas pela tela de Permissões, se precisar).
        </p>
      )}
      {form.perfil !== 'master' && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
            Instituições vinculadas
          </label>
          <div className="flex flex-wrap gap-2">
            {instituicoes.length === 0 && (
              <span className="text-sm text-gray-400 dark:text-gray-500">
                Nenhuma instituição cadastrada.
              </span>
            )}
            {instituicoes.map((inst) => (
              <label
                key={inst.id}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={form.instituicoes.includes(inst.id)}
                  onChange={() => toggleInstituicao(inst.id)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300" translate="no">
                  {inst.nome}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {form.perfil === 'professor' && (
        <div>
          <Select
            label="Educador vinculado"
            value={form.id_professor}
            onChange={(e) => setForm({ ...form, id_professor: e.target.value })}
            options={[
              {
                value: '',
                label:
                  form.instituicoes.length === 0
                    ? 'Selecione uma instituição primeiro...'
                    : 'Nenhum (sem vínculo)',
              },
              ...professoresDisponiveis.map((p) => ({ value: p.id, label: p.nome })),
            ]}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
            Liga esta conta de login ao cadastro de educador usado nas turmas — sem isso, telas como
            "Grade" não sabem quais turmas são deste educador.
          </p>
        </div>
      )}
      {form.perfil === 'coordenador' && (
        <div>
          <Select
            label="Área de coordenação"
            value={form.area_coordenacao}
            onChange={(e) => setForm({ ...form, area_coordenacao: e.target.value })}
            options={[
              { value: '', label: 'Geral (todas as áreas)' },
              ...AREAS.map((a) => ({ value: a.value, label: a.label })),
            ]}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
            Coordenador geral vê e edita pontos de todas as áreas. Escolha uma área para restringir
            esta conta só aos educadores daquela área (ex.: Coordenador Educacional).
          </p>
        </div>
      )}
      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <Check className="w-4 h-4" />
          {editando ? 'Salvar alterações' : 'Criar usuário'}
        </button>
        {editando && (
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
          >
            Cancelar
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        {erro && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {erro}
          </div>
        )}
        {mensagem && (
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400 text-sm flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            {mensagem}
          </div>
        )}

        {usuariosPendentes.length > 0 && (
          <div className="bg-amber-50/60 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40 rounded-2xl p-2.5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                Atenção: {usuariosPendentes.length} cadastro
                {usuariosPendentes.length > 1 ? 's' : ''} pendente
                {usuariosPendentes.length > 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-3">
              {usuariosPendentes.map((u) => (
                <div
                  key={u.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-white dark:bg-gray-900 border border-amber-100 dark:border-amber-900/40"
                >
                  <div>
                    <div
                      className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                      translate="no"
                    >
                      {u.nome}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {u.email} · {todosPerfis.find((p) => p.value === u.perfil)?.label || u.perfil}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => aprovarUsuario(u.id)}
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" /> Aprovar
                    </button>
                    <button
                      onClick={() => rejeitarUsuario(u.id)}
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Rejeitar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Modal isOpen={modalNovoAberto} onClose={resetForm} title="Novo Usuário" maxWidth="lg">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {camposFormulario}
          </form>
        </Modal>

        <Modal isOpen={!!editando} onClose={resetForm} title="Editar Usuário" maxWidth="lg">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {camposFormulario}
          </form>
        </Modal>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Usuários cadastrados
              </h2>
              <Badge tone="neutral">
                {usuariosFiltrados.length} de {usuarios.length}
              </Badge>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={UserPlus}
              onClick={() => setModalNovoAberto(true)}
            >
              Novo Usuário
            </Button>
          </div>

          <div className="p-3 pb-0 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
            <div className="w-full sm:w-64">
              <Input
                placeholder="Buscar por nome ou e-mail..."
                icon={Search}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                value={filtroPerfil}
                onChange={(e) => setFiltroPerfil(e.target.value)}
                options={[
                  { value: '', label: 'Todos os perfis' },
                  ...todosPerfis.map((p) => ({ value: p.value, label: p.label })),
                ]}
              />
            </div>
            <div className="w-full sm:w-52">
              <Select
                value={filtroInstituicao}
                onChange={(e) => setFiltroInstituicao(e.target.value)}
                options={[
                  { value: '', label: 'Todas as instituições' },
                  ...instituicoes.map((i) => ({ value: i.id, label: i.nome })),
                ]}
              />
            </div>
            <div className="w-full sm:w-36">
              <Select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                options={[
                  { value: '', label: 'Todos os status' },
                  { value: 'ativo', label: 'Ativo' },
                  { value: 'inativo', label: 'Inativo' },
                ]}
              />
            </div>
            {filtrosAtivos && (
              <button
                type="button"
                onClick={limparFiltros}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              >
                Limpar filtros
              </button>
            )}
          </div>

          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3" />
                <span className="text-sm">Carregando usuários...</span>
              </div>
            ) : usuarios.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                Nenhum usuário encontrado.
              </div>
            ) : usuariosFiltrados.length === 0 ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nenhum usuário encontrado para esses filtros.</p>
                <button
                  type="button"
                  onClick={limparFiltros}
                  className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline mt-1"
                >
                  Limpar filtros
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {usuariosFiltrados.map((u) => (
                  <div
                    key={u.id}
                    className={`p-2 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/30 ${u.status === 'inativo' ? 'opacity-60' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <div
                          className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                          translate="no"
                        >
                          {u.nome}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge tone={u.status === 'ativo' ? 'success' : 'neutral'}>
                          {u.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium w-fit">
                          {todosPerfis.find((p) => p.value === u.perfil)?.label || u.perfil}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Instituições:</span>{' '}
                      {u.perfil === 'master' ? (
                        <span>Todas</span>
                      ) : u.instituicoes && u.instituicoes.length > 0 ? (
                        <span className="text-gray-500 dark:text-gray-400">
                          {u.instituicoes.map(nomeInstituicao).join(', ')}
                        </span>
                      ) : (
                        <span className="text-red-500 dark:text-red-400">Nenhuma vinculada</span>
                      )}
                    </div>
                    {u.perfil === 'professor' && (
                      <div className="mt-1 text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          Educador vinculado:
                        </span>{' '}
                        {u.nome_professor ? (
                          <span className="text-gray-500 dark:text-gray-400" translate="no">
                            {u.nome_professor}
                          </span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400">
                            Nenhum — turmas não aparecerão na Grade dele
                          </span>
                        )}
                      </div>
                    )}
                    {u.perfil === 'coordenador' && (
                      <div className="mt-1 text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          Área de coordenação:
                        </span>{' '}
                        <span className="text-gray-500 dark:text-gray-400">
                          {u.area_coordenacao
                            ? AREA_LABEL[u.area_coordenacao] || u.area_coordenacao
                            : 'Geral (todas as áreas)'}
                        </span>
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => iniciarEdicao(u)}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 text-xs font-medium transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      <button
                        onClick={() => setSenhaModal(u.id)}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium transition-colors"
                      >
                        <Lock className="w-3.5 h-3.5" /> Redefinir senha
                      </button>
                      {u.perfil !== 'master' && (
                        <button
                          onClick={() => alternarStatusUsuario(u)}
                          disabled={alternandoStatusId === u.id}
                          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                            u.status === 'ativo'
                              ? 'bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                              : 'bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400'
                          }`}
                        >
                          {u.status === 'ativo' ? (
                            <PowerOff className="w-3.5 h-3.5" />
                          ) : (
                            <Power className="w-3.5 h-3.5" />
                          )}
                          {u.status === 'ativo' ? 'Desativar' : 'Reativar'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Modal
        isOpen={!!senhaModal}
        onClose={() => setSenhaModal(null)}
        title="Redefinir senha"
        maxWidth="sm"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const senha = e.target.senha.value;
            if (senha.length < 6) {
              setErro('A senha deve ter pelo menos 6 caracteres.');
              return;
            }
            redefinirSenha(senhaModal, senha);
          }}
        >
          <Input type="password" name="senha" label="Nova senha" required minLength="6" />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setSenhaModal(null)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default AdminUsuarios;
